import { apiPatch, apiPost } from "@/lib/apiClient";
import {
  assertContactDraftValid,
  contactDraftToApiBody,
  type LeadAttachedContactDraft,
} from "@/lib/crmLeadCreatePayload";
import {
  assertValidCrmChannelEmails,
  contactChannelsToApiBody,
  emptyChannelsState,
  type CrmContactChannelsState,
} from "@/lib/crmContactChannels";
import {
  assertValidCrmAddresses,
  legacyCityStateFromAddresses,
} from "@/lib/crmContactAddresses";
import { normalizeCrmSourceForSave } from "@/lib/crmDisplay";
import {
  normalizeDomainFieldInput,
  validateLeadAccountWebFields,
} from "@/lib/crmDomainFromWebsite";
import { crmLeadIndustryValueFromLegacyText } from "@/lib/crmLeadModuleFields";
import {
  CRM_LEAD_CHANNELS_SOURCE_DETAIL_MARKER,
  parseLeadSourceDetailChannelOverflow,
  type LeadSourceDetailChannelOverflow,
} from "@/lib/crmLeadSourceDetailChannels";

export type LeadEditContactRow = LeadAttachedContactDraft & {
  contactId: string | null;
  /** Stable React key; not sent to API. */
  rowKey: string;
};

export function createEmptyLeadEditContactRow(): LeadEditContactRow {
  return {
    rowKey: crypto.randomUUID(),
    contactId: null,
    firstName: "",
    lastName: "",
    title: "",
    channels: emptyChannelsState("contact"),
  };
}

function leadEditContactRowIsEmpty(row: LeadEditContactRow): boolean {
  const hasName = Boolean(
    [row.firstName.trim(), row.lastName.trim()].filter(Boolean).join(" ").trim(),
  );
  if (hasName || row.title.trim()) return false;
  const filledEmails = row.channels.emails.some((e) => e.address.trim());
  const filledPhones = row.channels.phones.some((p) => p.number.trim());
  const filledSocials = row.channels.socials.some((s) => s.url.trim());
  const filledAddresses = row.channels.addresses.some((a) =>
    [a.line1, a.line2, a.locality, a.adminArea, a.postalCode, a.recipientName].some((part) =>
      part?.trim(),
    ),
  );
  return !(filledEmails || filledPhones || filledSocials || filledAddresses);
}

/** Keeps `leadName` in API; derived from company or primary contact on save. */
export function deriveLeadNameForSave(input: {
  showCompany: boolean;
  companyName: string;
  contacts: LeadEditContactRow[];
  existingLeadName?: string | null;
}): string {
  const company = input.showCompany ? input.companyName.trim() : "";
  if (company) return company;

  for (const row of input.contacts) {
    if (leadEditContactRowIsEmpty(row)) continue;
    const name = [row.firstName.trim(), row.lastName.trim()].filter(Boolean).join(" ").trim();
    if (name) return name;
  }

  const existing = input.existingLeadName?.trim();
  if (existing) return existing.replace(/\s+Lead\s*$/i, "").trim() || existing;

  return "Lead";
}

export type LeadEditFormSnapshot = {
  leadId: string;
  accountId: string | null;
  leadType: string;
  existingLeadName: string;
  source: string;
  stage: string;
  status: string;
  score: number;
  ownerId: string;
  existingSourceDetail: string | null;
  showCompany: boolean;
  company: {
    name: string;
    domain: string;
    website: string;
    industry: string;
    industrySegment: string;
    channels: CrmContactChannelsState;
  };
  contacts: LeadEditContactRow[];
  industryOptions: { value: string; label: string }[];
};

function hasExtraChannels(
  channels: CrmContactChannelsState,
  legacy: { email: string | null; phone: string | null; linkedinUrl: string | null },
): boolean {
  const filledEmails = channels.emails.filter((e) => e.address.trim());
  const filledPhones = channels.phones.filter((p) => p.number.trim());
  const filledSocials = channels.socials.filter((s) => s.url.trim());
  if (filledEmails.length > 1) return true;
  if (filledPhones.length > 1) return true;
  if (filledSocials.length > 1) return true;
  if (filledPhones.some((p) => p.smsOptIn)) return true;
  if (filledSocials.some((s) => s.platform !== "linkedin")) return true;
  if (filledEmails.length === 1 && legacy.email && filledEmails[0].address !== legacy.email) return true;
  return false;
}

function buildSourceDetail(overflow: LeadSourceDetailChannelOverflow | null): string | null {
  if (!overflow || Object.keys(overflow).length === 0) return null;
  return `${CRM_LEAD_CHANNELS_SOURCE_DETAIL_MARKER}${JSON.stringify(overflow)}`;
}

function contactRowToSnapshot(row: LeadEditContactRow): Record<string, unknown> {
  const apiChannels = contactChannelsToApiBody(row.channels);
  const fullName = [row.firstName.trim(), row.lastName.trim()].filter(Boolean).join(" ").trim();
  return {
    contactId: row.contactId,
    firstName: row.firstName.trim() || null,
    lastName: row.lastName.trim() || null,
    fullName: fullName || null,
    title: row.title.trim() || null,
    email: apiChannels.email,
    phone: apiChannels.phone,
    linkedinUrl: apiChannels.linkedinUrl,
  };
}

function contactRowToContactPatch(row: LeadEditContactRow): Record<string, unknown> {
  const apiChannels = contactChannelsToApiBody(row.channels);
  return {
    firstName: row.firstName.trim() || null,
    lastName: row.lastName.trim() || null,
    title: row.title.trim() || null,
    email: apiChannels.email,
    phone: apiChannels.phone,
    linkedinUrl: apiChannels.linkedinUrl,
    emails: apiChannels.emails,
    phones: apiChannels.phones,
    socials: apiChannels.socials,
    addresses: apiChannels.addresses,
  };
}

export async function persistLeadEdit(form: LeadEditFormSnapshot): Promise<{ ok: true } | { ok: false; error: string }> {
  const contactRows = form.contacts.filter((row) => !leadEditContactRowIsEmpty(row));

  for (let i = 0; i < contactRows.length; i += 1) {
    const label = contactRows.length > 1 ? `Lead Contact ${i + 1}` : "Lead Contact";
    try {
      assertContactDraftValid(contactRows[i], label);
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : "Contact validation failed." };
    }
  }

  const webFieldError = validateLeadAccountWebFields({
    website: form.company.website,
    domain: form.company.domain,
  });
  if (webFieldError && form.showCompany) {
    return { ok: false, error: webFieldError };
  }

  try {
    for (let i = 0; i < contactRows.length; i += 1) {
      const label = contactRows.length > 1 ? `Lead Contact ${i + 1} email` : "Lead Contact email";
      assertValidCrmChannelEmails(contactRows[i].channels, label);
      const addressLabel =
        contactRows.length > 1 ? `Lead Contact ${i + 1} address` : "Lead Contact address";
      assertValidCrmAddresses(contactRows[i].channels.addresses, addressLabel);
    }
    if (form.showCompany) {
      assertValidCrmChannelEmails(form.company.channels, "Lead Account email");
      assertValidCrmAddresses(form.company.channels.addresses, "Lead Account address");
    }
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "Validation failed." };
  }

  const normalizedIndustry = crmLeadIndustryValueFromLegacyText(
    form.company.industry,
    form.industryOptions,
  );

  const existingOverflow = parseLeadSourceDetailChannelOverflow(form.existingSourceDetail) ?? {};
  const overflow: LeadSourceDetailChannelOverflow = { ...existingOverflow };

  const isAccountLead =
    form.leadType === "company_only" || form.leadType === "company_plus_contact";

  if (form.showCompany) {
    const companyChannels = contactChannelsToApiBody(form.company.channels);
    if (hasExtraChannels(form.company.channels, companyChannels)) {
      overflow.company = {
        emails: form.company.channels.emails.filter((e) => e.address.trim()),
        phones: form.company.channels.phones.filter((p) => p.number.trim()),
        socials: form.company.channels.socials.filter((s) => s.url.trim()),
        addresses: companyChannels.addresses,
      };
    } else {
      delete overflow.company;
    }
  }

  const attachedOverflow: NonNullable<LeadSourceDetailChannelOverflow["attachedContacts"]> = [];
  contactRows.forEach((row, index) => {
    const apiChannels = contactChannelsToApiBody(row.channels);
    const bucket = hasExtraChannels(row.channels, apiChannels)
      ? {
          emails: row.channels.emails.filter((e) => e.address.trim()),
          phones: row.channels.phones.filter((p) => p.number.trim()),
          socials: row.channels.socials.filter((s) => s.url.trim()),
          addresses: apiChannels.addresses,
        }
      : null;

    if (isAccountLead) {
      if (bucket) attachedOverflow[index] = bucket;
    } else if (index === 0) {
      if (bucket) overflow.contact = bucket;
      else delete overflow.contact;
    }
  });

  if (isAccountLead) {
    if (attachedOverflow.some(Boolean)) {
      overflow.attachedContacts = attachedOverflow;
    } else {
      delete overflow.attachedContacts;
    }
  }

  const sourceDetail = buildSourceDetail(overflow);

  const resolvedContacts: LeadEditContactRow[] = [];
  const hasExistingLinkedContact = contactRows.some((row) => row.contactId);
  for (let index = 0; index < contactRows.length; index += 1) {
    const row = contactRows[index];
    if (row.contactId || !form.accountId) {
      resolvedContacts.push(row);
      continue;
    }

    const createRes = await apiPost<{ contact?: { id?: string } }>("/api/crm/contacts", {
      accountId: form.accountId,
      kind: "person",
      isPrimary: !hasExistingLinkedContact && index === 0,
      ...contactDraftToApiBody(row),
    });
    const newContactId = createRes.data?.contact?.id;
    if (!createRes.ok || !newContactId) {
      const label = contactRows.length > 1 ? `Lead Contact ${index + 1}` : "Lead Contact";
      return {
        ok: false,
        error: createRes.error || `Unable to create ${label} on the Lead Account.`,
      };
    }
    resolvedContacts.push({ ...row, contactId: newContactId });
  }

  const leadName = deriveLeadNameForSave({
    showCompany: form.showCompany,
    companyName: form.company.name,
    contacts: resolvedContacts,
    existingLeadName: form.existingLeadName,
  });

  const leadPatch: Record<string, unknown> = {
    leadName,
    stage: form.stage,
    status: form.status,
    score: form.score,
    ownerId: form.ownerId.trim() === "" ? null : form.ownerId.trim(),
    source: normalizeCrmSourceForSave(form.source),
    sourceDetail,
    contacts: resolvedContacts.map(contactRowToSnapshot),
  };

  if (form.showCompany) {
    leadPatch.companyName = form.company.name.trim() === "" ? null : form.company.name.trim();
    leadPatch.companyDomain =
      normalizeDomainFieldInput(form.company.domain) === ""
        ? null
        : normalizeDomainFieldInput(form.company.domain);
    leadPatch.companyWebsite = form.company.website.trim() === "" ? null : form.company.website.trim();
    leadPatch.companyIndustry =
      normalizedIndustry.trim() === "" ? null : normalizedIndustry.trim();
  }

  const leadRes = await apiPatch<{ id: string }>(
    `/api/crm/leads/${encodeURIComponent(form.leadId)}`,
    leadPatch,
  );
  if (!leadRes.ok) {
    return { ok: false, error: leadRes.error || "Unable to save lead." };
  }

  if (form.accountId && form.showCompany) {
    const companyChannels = contactChannelsToApiBody(form.company.channels);
    const location = legacyCityStateFromAddresses(companyChannels.addresses);
    const accountRes = await apiPatch(
      `/api/crm/accounts/${encodeURIComponent(form.accountId)}`,
      {
        name: form.company.name.trim(),
        domain:
          normalizeDomainFieldInput(form.company.domain) === ""
            ? null
            : normalizeDomainFieldInput(form.company.domain),
        website: form.company.website.trim() === "" ? null : form.company.website.trim(),
        industry: normalizedIndustry.trim() === "" ? null : normalizedIndustry.trim(),
        industrySegment:
          form.company.industrySegment.trim() === "" ? null : form.company.industrySegment.trim(),
        email: companyChannels.email,
        companyPhone: companyChannels.phone,
        city: location.city || null,
        state: location.state || null,
        addresses: companyChannels.addresses,
      },
    );
    if (!accountRes.ok) {
      return {
        ok: false,
        error: accountRes.error || "Lead saved, but Lead Account details could not be updated.",
      };
    }
  }

  if (form.accountId) {
    for (const row of resolvedContacts) {
      if (!row.contactId) continue;
      const contactRes = await apiPatch(
        `/api/crm/contacts/${encodeURIComponent(row.contactId)}?accountId=${encodeURIComponent(form.accountId)}`,
        contactRowToContactPatch(row),
      );
      if (!contactRes.ok) {
        return {
          ok: false,
          error:
            contactRes.error ||
            "Lead saved, but one or more Lead Contacts could not be updated.",
        };
      }
    }
  }

  return { ok: true };
}
